/**
 * MarketingEditor — the one editor for a Unit's marketing profile (BR-110), used by the Unit's
 * Marketing tab and by the listing guide's Marketing step: one UI for one data.
 *
 * Every field group is controlled by one lifted draft and a single save persists every edited part
 * (description, details, amenities). It reports its unsaved-edits flag, and exposes `GuideStepHandle`
 * so the guide's "Save and continue" can save it. Photos are not part of the draft: they upload and
 * save as they are added (see PhotoGrid); `photosSlot` places them between Description and the detail sections.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { useMarketingProfile } from '../../hooks/useMarketingProfile';
import type { GuideStepHandle } from '../../pages/leasing/listing-guide/guide-steps';
import type { UnitMarketingProfile } from '../../types/unit-marketing.types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ProfileSectionAmenities } from './ProfileSectionAmenities';
import { ProfileSectionClimateAndLaundry } from './ProfileSectionClimateAndLaundry';
import { ProfileSectionDescription } from './ProfileSectionDescription';
import { ProfileSectionParking } from './ProfileSectionParking';
import { ProfileSectionPropertyDetails } from './ProfileSectionPropertyDetails';
import { ProfileSectionRestrictions } from './ProfileSectionRestrictions';
import {
  draftToUpdates,
  isDraftDirty,
  normalizeDraft,
  profileToDraft,
  validateDraft,
  type MarketingDraft,
} from './marketingDraft';

interface Props {
  unitId: string;
  /** Label of the editor's own save button. Default "Save changes". */
  saveLabel?: string;
  /** The guide saves through its "Save and continue"; it hides the editor's own button and badge. */
  hideSaveButton?: boolean;
  /** Rendered between the Description card and the first detail section (the guide shows Photos there). */
  photosSlot?: ReactNode;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

interface FormProps extends Omit<Props, 'unitId'> {
  profile: UnitMarketingProfile;
  isSaving: boolean;
  saveProfile: (updates: Partial<UnitMarketingProfile>) => Promise<void>;
}

const MarketingEditorForm = forwardRef<GuideStepHandle, FormProps>(function MarketingEditorForm(
  { profile, isSaving, saveProfile, saveLabel = 'Save changes', hideSaveButton, photosSlot, onSaved, onDirtyChange, onSavingChange },
  ref
) {
  const [baseline, setBaseline] = useState<MarketingDraft>(() => profileToDraft(profile));
  const [draft, setDraft] = useState<MarketingDraft>(baseline);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const dirty = isDraftDirty(draft, baseline);
  const latest = useRef({ draft, dirty, profile });
  latest.current = { draft, dirty, profile };

  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  useEffect(() => onSavingChange?.(isSaving), [isSaving, onSavingChange]);
  useEffect(() => {
    if (!justSaved) return;
    const timer = setTimeout(() => setJustSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const change = useCallback((patch: Partial<MarketingDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setJustSaved(false);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const current = latest.current;
    setSaveError(null);
    const invalid = validateDraft(current.draft);
    setUrlError(invalid?.virtualTourUrl ?? null);
    if (invalid) return false;
    if (!current.dirty) return true;
    try {
      await saveProfile(draftToUpdates(current.draft, current.profile));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
      return false;
    }
    const saved = normalizeDraft(current.draft);
    setBaseline(saved);
    setDraft(saved);
    setJustSaved(true);
    onSaved?.();
    return true;
  }, [saveProfile, onSaved]);

  useImperativeHandle(ref, () => ({ save, isDirty: () => latest.current.dirty }), [save]);

  return (
    <div className="flex flex-col gap-4" data-testid="marketing-profile-form">
      <ProfileSectionDescription draft={draft} onChange={change} />
      {photosSlot}
      <ProfileSectionAmenities draft={draft} onChange={change} />
      <ProfileSectionPropertyDetails draft={draft} onChange={change} urlError={urlError} />
      <ProfileSectionClimateAndLaundry draft={draft} onChange={change} />
      <ProfileSectionParking draft={draft} onChange={change} />
      <ProfileSectionRestrictions draft={draft} onChange={change} />

      {saveError && (
        <div
          className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2"
          role="alert"
          data-testid="marketing-profile-save-error"
        >
          <p className="text-sm text-destructive">{saveError}</p>
        </div>
      )}

      {!hideSaveButton && (
        <div className="flex items-center justify-end gap-3">
          {dirty && (
            <Badge variant="warning" data-testid="marketing-profile-unsaved-badge">
              Unsaved changes
            </Badge>
          )}
          <Button onClick={() => void save()} disabled={isSaving} data-testid="save-marketing-profile">
            {isSaving ? 'Saving...' : justSaved ? 'Saved!' : saveLabel}
          </Button>
        </div>
      )}
    </div>
  );
});

export const MarketingEditor = forwardRef<GuideStepHandle, Props>(function MarketingEditor({ unitId, ...rest }, ref) {
  const { profile, isLoading, isSaving, error, saveProfile, refetch } = useMarketingProfile(unitId);

  if (isLoading || (!profile && !error)) {
    return (
      <div className="space-y-3" data-testid="marketing-profile-form" aria-busy="true">
        <Skeleton className="h-60 w-full" />
        <Skeleton className="h-60 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div
        className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-4"
        data-testid="marketing-profile-error"
        role="alert"
      >
        <p className="mb-3 text-sm text-destructive">{error}</p>
        <Button variant="outline" onClick={() => void refetch()} data-testid="marketing-profile-retry">
          Retry
        </Button>
      </div>
    );
  }

  return <MarketingEditorForm ref={ref} profile={profile} isSaving={isSaving} saveProfile={saveProfile} {...rest} />;
});
